/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import React, {useCallback, useEffect, useState} from 'react'
import styled from "styled-components";
import {useSearchParams} from "react-router-dom";
import Description from "./Description";
import Configure from "./Configure";
import {useAppSelector} from "../../../../redux/hooks";
import {selectGlobalDialogState} from "../../../../redux/ducks/GlobalDialog/slice";
import CloseGlobalDialogButton from "../../../components/Global/Dialog/CloseGlobalDialogButton";
import {createSelector} from 'reselect';
import {Typography} from '@mui/material';
import SelectModel from './SelectModel';
import {SCENARIO_ANALYSIS_CONSTANTS} from '../constants';
import {cloneDeep} from 'lodash';
import {HorizontalTabs} from '../../../components/Tabs/HorizontalTabs';
import {RootState} from '../../../../redux/store';
import {isShapeModel, ModelData} from '../../../../helpers/models';
import AppBar from '../../../components/AppBar/AppBar';
import {usePostRequest} from '../../../../api/helper';
import toast from 'react-hot-toast';
import {ISetRecentlyViewedParams, useSetRecentlyViewedWithDeps} from "../../../../hooks/useSetRecentlyViewedWithDeps";
import {ETrackedItemTypes} from '../../../../constants/trackedItems';


const Container = styled.div({
  display: 'flex', flexDirection: 'column', height: '100%', width: '100%',
})


const createTabs = (_data: ModelData) => {
  const tab1 = {
    name: "Configure",
    content: <Configure model={_data.model} version={_data.version} variation={_data.variation}/>
  }
  const tab2 = {
    name: "Description",
    content: <Description model={_data.model} version={_data.version} variation={_data.variation}/>
  }
  
  if (isShapeModel(_data?.model)) {
    var _horizontalTabs = [tab1]
  } else {
    var _horizontalTabs = [tab1, tab2]
  }
  return _horizontalTabs
}


const icons = (
  <div style={{display: 'flex', alignItems: 'center'}}>
    <CloseGlobalDialogButton/>
  </div>
)


const selectDialogState = (state: RootState) => selectGlobalDialogState(state);


const selectUrlParams = createSelector(
  (state: RootState, searchParams: URLSearchParams) => searchParams,
  (searchParams) => ({
    m: searchParams.get('m') ?? "",
    ver: searchParams.get('ver') ?? "",
    var: searchParams.get('var') ?? "",
  })
)


const selectParams = createSelector(
  [selectDialogState, selectUrlParams],
  (dialogState, urlParams) => {
    const dialogParams = dialogState.isOpen ? dialogState.component?.params ?? {} : {}
    return {
      m: urlParams.m || dialogParams.m || "",
      ver: urlParams.ver || dialogParams.ver || "",
      var: urlParams.var || dialogParams.var || "",
    }
  }
)


/**
 * Root component for the View Model page
 */
const ViewModel = () => {
  const [searchParams, setSearchParams] = useSearchParams()

  const params = useAppSelector(state => selectParams(state, searchParams))
  const model = params?.m
  const version = params?.ver
  const variation = params?.var
  const [selectedModel, setSelectedModel] = useState<ModelData>({model, version, variation})
  const [trackedItemId, setTrackedItemId] = useState<string>("")
  const [tabs, setTabs] = useState<any>()


  /**
   * Fetch tracked item ID for selected model
   */
  const url = SCENARIO_ANALYSIS_CONSTANTS.viewModel.endpoints.modelTrackedItemId
  const fetchTrackedItemIdHandle = usePostRequest(url)
  const fetchTrackedItemId = (_data: ModelData) => {
    if (!_data.model || !_data.version || !_data.variation) {
      setTrackedItemId("")
      return
    }
    const body = {model: _data.model, version: _data.version, variationId: _data.variation}
    fetchTrackedItemIdHandle(body).then((resp: any) => {
      setTrackedItemId(resp.id)
    }).catch((e) => {
      console.log(e)
      toast.error("Tracked item not created for this model. Please contact an administrator.")
    })
  }


  /**
   * Update tabs when the model, version, or variation changes
   */
  useEffect(() => {
    fetchTrackedItemId(selectedModel)
    setTabs(createTabs(selectedModel))
  }, [selectedModel])


  /** 
   * Update Recently Viewed with currently selected model
   */
  const recentlyViewedParams: ISetRecentlyViewedParams = {
    trackedItemUid: trackedItemId,
    partitionKey: trackedItemId,
    trackedItemType: ETrackedItemTypes.SCENARIO_ANALYSIS,
  }
  useSetRecentlyViewedWithDeps(recentlyViewedParams)


  /**
   * Update the page content when the model, version, or variation changes
   */
  const handleModelChange = useCallback((key: string, value: string) => {
    let newScenarioData: ModelData = {"model": "", "version": "", "variation": ""}
    const newSearchParams = new URLSearchParams(searchParams)
    switch (key) {
      case "model":
        newScenarioData = cloneDeep(selectedModel)
        newScenarioData = Object.assign(newScenarioData, {
          "model": value,
          "version": "",
          "variation": ""
        })
        newSearchParams.set('m', value)
        newSearchParams.delete('ver')
        newSearchParams.delete('var')
        setSearchParams(newSearchParams)
        break
      case "version":
        newScenarioData = cloneDeep(selectedModel)
        newScenarioData = Object.assign(newScenarioData, {
          "version": value,
          "variation": ""
        })
        newSearchParams.set('ver', value)
        newSearchParams.delete('var')
        setSearchParams(newSearchParams)
        break
      case "variation":
        newScenarioData = cloneDeep(selectedModel)
        newScenarioData = Object.assign(newScenarioData, {
          "variation": value,
        })
        newSearchParams.set('var', value)
        setSearchParams(newSearchParams)
        break
    }
    setSelectedModel(newScenarioData)
  }, [selectedModel])

  useEffect(() => {
    document.title = SCENARIO_ANALYSIS_CONSTANTS.landing.title + ' - ' + SCENARIO_ANALYSIS_CONSTANTS.viewModel.title;
    return () => {
      document.title = 'Argos';
    };
  }, []);
  
  return (
    <Container>
      <div>
        <AppBar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, color: '#ffffff'}}>
            {SCENARIO_ANALYSIS_CONSTANTS.viewModel.title}
          </Typography>
          {icons}
        </AppBar>
      </div>
      <div style={{flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', height: '100%', width: '100%'}}>
        <SelectModel modelData={selectedModel} trackedItemId={trackedItemId} handleChange={handleModelChange} disableSelect={false}/>
        <HorizontalTabs initContent={null} initHtabIndex={0} initHtabName={'Configure'}
          horizontalTabs={tabs} align={'left'}
          uid={SCENARIO_ANALYSIS_CONSTANTS.viewModel.constants.states.tabId}
          contentDivProps={{display: 'flex', flexGrow: 1, flexDirection: 'column', overflow: 'hidden', height: '100%', width: '100%', scrollbarGutter: 'auto', margin: 0}}
          outerDivProps={{padding: 0}}
        />
      </div>
    </Container>
  )
}


export default ViewModel
